import React, { createContext, useContext, useRef, useState, useCallback } from "react";
import { api, BACKEND_URL } from "@/lib/apiClient";

const AvaContext = createContext(null);
export const useAva = () => useContext(AvaContext);

export function AvaProvider({ children }) {
  const [speaking, setSpeaking] = useState(false);
  const [caption, setCaption] = useState("");
  const [voiceEnabled, setVoiceEnabled] = useState(() => localStorage.getItem("octon_voice") === "on");
  const audioRef = useRef(null);

  const toggleVoice = useCallback(() => {
    setVoiceEnabled((v) => {
      const nv = !v;
      localStorage.setItem("octon_voice", nv ? "on" : "off");
      if (!nv && audioRef.current) { audioRef.current.pause(); setSpeaking(false); setCaption(""); }
      return nv;
    });
  }, []);

  const speak = useCallback(async (text, opts = {}) => {
    if (!text) return;
    // opt-in: auto narration only fires when voice is enabled; explicit buttons pass { force: true }
    if (!opts.force && localStorage.getItem("octon_voice") !== "on") return;
    try {
      setCaption(text.slice(0, 160));
      const res = await api.post("/ava/speak", { text });
      const url = `${BACKEND_URL}${res.data.url}`;
      if (audioRef.current) { audioRef.current.pause(); }
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onplay = () => setSpeaking(true);
      audio.onended = () => { setSpeaking(false); setCaption(""); };
      audio.onerror = () => { setSpeaking(false); };
      await audio.play();
    } catch (e) {
      setSpeaking(false);
    }
  }, []);

  const stop = useCallback(() => {
    if (audioRef.current) audioRef.current.pause();
    setSpeaking(false);
    setCaption("");
  }, []);

  return (
    <AvaContext.Provider value={{ speak, stop, speaking, caption, voiceEnabled, toggleVoice }}>
      {children}
    </AvaContext.Provider>
  );
}
